import React, { forwardRef, PropsWithChildren } from 'react'
import { cva, VariantProps } from 'class-variance-authority'
import { cn } from '../../utils'

type ButtonProps = React.ComponentPropsWithoutRef<'button'> &
  VariantProps<typeof buttonStyles> & {
    hidden?: boolean
  }

const buttonStyles = cva(
  'user-select-none flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg transition-all font-normal',
  {
    variants: {
      size: {
        icon: 'h-6 w-6 gap-1 rounded-md p-0.5',
        'padded-icon': 'h-8 w-8 gap-1 rounded-xl p-1',
        xsmall: 'text-[13px] h-6 gap-1.5 rounded-lg px-1.5',
        small: 'text-sm h-8 rounded-lg px-3 leading-none gap-1.5',
        medium: 'text-sm h-9 rounded-xl px-3 leading-none',
        large: 'text-sm h-10 rounded-xl px-4 leading-none',
        xlarge: 'h-12 rounded-2xl px-5 text-md',
        xround: 'h-10 rounded-3xl px-6',
      },
      variant: {
        primary:
          'bg-primary-10 text-primary-90 hover:bg-primary-20 hover:text-teal-subtle active:bg-primary-10 active:text-primary-60',
        'primary-outline':
          'text-primary border-primary-60 hover:border-primary-90 hover:bg-primary-5 active:border-primary-80 active:text-primary-80 border bg-transparent',
        secondary: 'bg-light-80 text-dark hover:bg-light active:bg-light-60',
        tertiary:
          'bg-light/10 text-light-80 hover:bg-light/20 hover:text-light active:bg-light/10 active:text-light-60',
        'tertiary-outline':
          'active:bg-light-10 active:text-light-80 bg-transparent text-white/30 outline outline-1 -outline-offset-1 outline-white/10 hover:bg-white/10 hover:text-white hover:outline-white/0 hover:outline-white/20',
        ghost:
          'text-primary-80 hover:bg-primary-10 hover:text-primary active:bg-primary-5 active:text-primary-60 bg-transparent',
        'ghost-neutral':
          'text-foreground-placeholder hover:bg-element-hover hover:text-foreground active:bg-element-hover active:text-foreground-secondary bg-transparent',
        'ghost-neutral-outline':
          'text-foreground-placeholder hover:bg-element-hover hover:text-foreground active:bg-element-hover active:text-foreground-secondary bg-transparent outline outline-1 -outline-offset-1 outline-foreground/10 hover:outline-foreground/0 hover:outline-foreground/20',
        danger: 'text-red hover:bg-red/15 hover:text-red active:bg-red-3 active:text-red-80 bg-red-20',
        success: 'bg-green-3 text-green hover:bg-green-4 hover:text-green active:bg-green-2 active:text-green-80',
        purple: 'bg-purple-80 text-light-80 hover:bg-purple hover:text-light active:bg-purple-60 active:text-light-80',
        accent: 'bg-bg-layer-1 text-primary hover:bg-[rgba(0,225,252,0.2)]',
        highlighted: 'bg-yellow hover:bg-yellow-80',
        yellow:
          'bg-yellow/90 text-black hover:bg-yellow shadow-none hover:shadow-lg hover:shadow-yellow/10 transition-[box-shadow,color,background-color] duration-300',
        'yellow-secondary': 'bg-yellow/10 text-yellow-subtle/90 hover:bg-yellow/20',
        'yellow-ghost': 'text-yellow-subtle hover:bg-yellow/10 bg-transparent',
        'yellow-accent': 'bg-bg-layer-1 text-primary hover:bg-yellow/20',
      },
      disabled: {
        true: 'opacity-35 pointer-events-none',
        false: '',
      },
      hidden: {
        true: 'hidden',
        false: '',
      },
    },
    defaultVariants: {
      size: 'medium',
      variant: 'primary',
      disabled: false,
      hidden: false,
    },
  },
)

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  ({ size, variant, disabled, hidden, className, children, ...rest }, ref) => {
    return (
      <button ref={ref} className={cn(buttonStyles({ size, variant, disabled, hidden }), className)} {...rest}>
        {children}
      </button>
    )
  },
)
